import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { MessageSquare, Send, ArrowLeft } from "lucide-react";

import { cn } from "lib/utils";
import { getAvatarUrl } from "helpers/avatar";
import {
  getDirectContacts,
  getDirectConversation,
  sendDirectMessage,
} from "slices/communication/thunk";
import { DirectChatUser, DirectMessage } from "slices/communication/types";

const formatTime = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const Communication = () => {
  document.title = "Communication | EngageHub IQ";

  const dispatch = useDispatch<any>();
  const location = useLocation();
  const incomingUserId = (location.state as { userId?: string } | null)?.userId;
  const { contacts, messages, activeUserId, loadingMessages } = useSelector(
    (state: any) => state.Communication
  );

  const [draft, setDraft] = useState("");
  const [showThreadMobile, setShowThreadMobile] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(getDirectContacts());
  }, [dispatch]);

  // Auto-open a thread when navigated here from the Friends page.
  useEffect(() => {
    if (incomingUserId) {
      dispatch(getDirectConversation(incomingUserId));
      setShowThreadMobile(true);
    }
  }, [incomingUserId, dispatch]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const activeContact = useMemo(
    () => contacts.find((c: DirectChatUser) => c.userId === activeUserId),
    [contacts, activeUserId]
  );

  const handleSelect = (contact: DirectChatUser) => {
    dispatch(getDirectConversation(contact.userId));
    setShowThreadMobile(true);
  };

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content || !activeUserId) return;
    dispatch(sendDirectMessage({ recipientId: activeUserId, content }));
    setDraft("");
  };

  return (
    <div className="-m-4 sm:-m-6 lg:-m-8">
      <div
        className={cn(
          "flex h-[calc(100vh-4rem)] w-full overflow-hidden",
          "border-y border-border/60 bg-card/30 backdrop-blur-sm xl:border xl:rounded-2xl"
        )}
      >
        {/* Contact list */}
        <div
          className={cn(
            "flex flex-col xl:w-80 xl:shrink-0 xl:border-r xl:border-border/60",
            showThreadMobile ? "hidden xl:flex" : "flex w-full"
          )}
        >
          <div className="border-b border-border/60 px-4 py-4">
            <h2 className="text-base font-semibold text-foreground">Communication</h2>
            <p className="text-xs text-muted-foreground">Internal direct messages</p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {contacts.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">No users available.</div>
            ) : (
              contacts.map((contact: DirectChatUser) => {
                const isActive = contact.userId === activeUserId;
                return (
                  <button
                    key={contact.userId}
                    type="button"
                    onClick={() => handleSelect(contact)}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors",
                      isActive ? "bg-primary/10" : "hover:bg-muted/60"
                    )}
                  >
                    <img
                      src={getAvatarUrl(contact.email || contact.userId)}
                      alt={contact.name}
                      className="h-10 w-10 shrink-0 rounded-full bg-muted"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium text-foreground">
                          {contact.name}
                        </span>
                        <span className="shrink-0 text-[11px] text-muted-foreground">
                          {formatTime(contact.lastMessageDateTime)}
                        </span>
                      </span>
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-xs text-muted-foreground">
                          {contact.lastMessageContent || "No messages yet"}
                        </span>
                        {contact.unreadCount > 0 && (
                          <span className="grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
                            {contact.unreadCount}
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Thread */}
        <div
          className={cn(
            "min-w-0 flex-1 flex-col",
            showThreadMobile ? "flex w-full" : "hidden xl:flex"
          )}
        >
          {activeContact ? (
            <>
              <div className="flex items-center gap-3 border-b border-border/60 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setShowThreadMobile(false)}
                  className="grid h-8 w-8 place-items-center rounded-md hover:bg-muted/70 xl:hidden"
                  aria-label="Back"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <img
                  src={getAvatarUrl(activeContact.email || activeContact.userId)}
                  alt={activeContact.name}
                  className="h-9 w-9 shrink-0 rounded-full bg-muted"
                />
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-foreground">
                    {activeContact.name}
                  </div>
                  {activeContact.email && (
                    <div className="truncate text-xs text-muted-foreground">
                      {activeContact.email}
                    </div>
                  )}
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
                {loadingMessages ? (
                  <div className="text-sm text-muted-foreground">Loading…</div>
                ) : messages.length === 0 ? (
                  <div className="grid h-full place-items-center text-sm text-muted-foreground">
                    Say hello to {activeContact.name}.
                  </div>
                ) : (
                  messages.map((message: DirectMessage) => (
                    <div
                      key={message.id}
                      className={cn(
                        "flex",
                        message.isMine ? "justify-end" : "justify-start"
                      )}
                    >
                      <div
                        className={cn(
                          "max-w-[75%] rounded-2xl px-3 py-2 text-sm",
                          message.isMine
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-foreground"
                        )}
                      >
                        <div className="whitespace-pre-wrap break-words">
                          {message.content}
                        </div>
                        <div
                          className={cn(
                            "mt-1 text-[10px]",
                            message.isMine
                              ? "text-primary-foreground/70"
                              : "text-muted-foreground"
                          )}
                        >
                          {formatTime(message.creationTime)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <form
                onSubmit={handleSend}
                className="flex items-center gap-2 border-t border-border/60 px-4 py-3"
              >
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type a message…"
                  className="flex-1 rounded-lg border border-border/60 bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                />
                <button
                  type="submit"
                  disabled={!draft.trim()}
                  className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-primary-foreground transition-opacity disabled:opacity-50"
                  aria-label="Send"
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </>
          ) : (
            <div className="hidden flex-1 items-center justify-center text-center xl:flex">
              <div className="max-w-sm space-y-3 px-6">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary">
                  <MessageSquare className="h-6 w-6" />
                </div>
                <h3 className="text-base font-semibold text-foreground">
                  Select a user
                </h3>
                <p className="text-sm text-muted-foreground">
                  Pick a teammate from the list to start a private conversation.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Communication;
